import { Component, OnInit } from '@angular/core';
import { CommonService } from 'src/app/provide/common.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-succes-customer-payment',
    templateUrl: './succes-customer-payment.component.html',
    styleUrls: ['./succes-customer-payment.component.scss'],
    standalone: false
})
export class SuccesCustomerPaymentComponent implements OnInit {
  paymentList: any;
  orderId: string;
  paymentMethod: string;
  
  constructor(
    public commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.paymentList = localStorage.getItem('paymentList');
    console.log('Payment list type:', this.paymentList);
    
    // Get payment method from localStorage
    this.paymentMethod = localStorage.getItem('paymentMethod');
    console.log('Payment method:', this.paymentMethod);
    
    // Critical safeguard: If isServiceBooking flag is set, force paymentList to 'booking'
    // This prevents cart clearing for service bookings even if paymentList was incorrectly set
    const isServiceBooking = localStorage.getItem('isServiceBooking') === 'true';
    if (isServiceBooking) {
      this.paymentList = 'booking';
      localStorage.setItem('paymentList', 'booking');
      console.log('Forced payment list to booking based on isServiceBooking flag');
    }
    
    // If paymentList is not set, try to determine it from the order type
    if (!this.paymentList) {
      // Default to 'booking' if dealing with Yoco payment for services
      if (this.paymentMethod === 'YOCO' && localStorage.getItem('from_yoco_payment') === 'true') {
        const fromBooking = localStorage.getItem('isServiceBooking') === 'true';
        this.paymentList = fromBooking ? 'booking' : 'product';
        console.log('Setting payment list type based on payment method:', this.paymentList);
      }
    }
    
    // If this is a wallet payment, refresh wallet transactions
    if (this.paymentList === 'wallet') {
      console.log('Wallet payment detected, refreshing wallet transactions');
      this.refreshWalletTransactions();
    }
    
    // First try to get orderId from URL params
    this.route.queryParams.subscribe(params => {
      this.orderId = params['orderId'];
      console.log('Order ID from URL:', this.orderId);
      
      // Check the payment method and handle accordingly
      if (this.paymentMethod === 'YOCO') {
        if (this.orderId) {
          // If we have orderId in URL, update that specific order
          console.log('Updating order from URL param:', this.orderId);
          this.updateOrderStatus(this.orderId);
        } else {
          // Fallback to finding the latest order
          console.log('No orderId in URL, trying to find latest order');
          this.findAndUpdateLatestOrder();
        }
      }
    });
  }

  /**
   * Find the latest order by this user and update its status
   * This ensures the order appears in order history after Yoco payment
   */
  findAndUpdateLatestOrder() {
    this.commonService.showSpinner();
    
    // Get the timestamp that was stored when the order was created
    const orderTimestamp = localStorage.getItem('orderTimestamp');
    const orderIdFromStorage = localStorage.getItem('lastOrderId');
    
    // If we have stored orderId, use that directly
    if (orderIdFromStorage) {
      console.log('Using orderId from localStorage:', orderIdFromStorage);
      this.updateOrderStatus(orderIdFromStorage);
      return;
    }
    
    // Get all orders regardless of status
    const url = 'order/orderListOfOwn';
    const data = {
      page: 1,
      limit: 20  // Increased limit to ensure we get the recent order
    };
    
    this.commonService.postApi(url, data, 1).subscribe(
      (res: any) => {
        if (res.responseCode === 200 && res.result && res.result.docs && res.result.docs.length > 0) {
          // Sort by createdAt descending to get the most recent orders
          const sortedOrders = res.result.docs.sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          
          let orderToUpdate;
          
          // If we have a timestamp, try to find an order created around that time
          if (orderTimestamp) {
            const timestampDate = new Date(orderTimestamp);
            // Look for orders created within 5 minutes of the stored timestamp
            orderToUpdate = sortedOrders.find(order => {
              const orderDate = new Date(order.createdAt);
              const timeDiff = Math.abs(orderDate.getTime() - timestampDate.getTime());
              // If the order was created within 5 minutes of the timestamp and is pending
              return timeDiff < 300000 && order.paymentStatus === 'PENDING';
            });
          }
          
          // If no matching order found by timestamp, fall back to the most recent pending order
          if (!orderToUpdate) {
            orderToUpdate = sortedOrders.find(order => order.paymentStatus === 'PENDING');
          }
          
          // If still no pending order found, use the most recent order
          if (!orderToUpdate) {
            orderToUpdate = sortedOrders[0];
          }
          
          if (orderToUpdate && orderToUpdate.paymentStatus !== 'COMPLETED') {
            console.log('Found order to update:', orderToUpdate._id);
            this.updateOrderStatus(orderToUpdate._id);
          } else {
            this.commonService.hideSpinner();
            if (orderToUpdate) {
              console.log('Order already has COMPLETED status');
            } else {
              console.log('No suitable order found to update');
            }
          }
        } else {
          this.commonService.hideSpinner();
          console.log('No orders found to update');
        }
      },
      (err) => {
        this.commonService.hideSpinner();
        console.error('Error checking latest order:', err);
      }
    );
  }

  /**
   * Update the order status to COMPLETED for Yoco payments
   */
  updateOrderStatus(orderId: string) {
    console.log('Updating order status for order:', orderId);
    const url = `order/updateOrderData?_id=${orderId}`;
    const data = {
      paymentStatus: 'COMPLETED'
    };
    
    this.commonService.putApi(url, data, 1).subscribe(
      (res: any) => {
        this.commonService.hideSpinner();
        if (res.responseCode === 200) {
          console.log('Order status updated successfully');
          
          // CRITICAL: Read isServiceBooking flag BEFORE clearing localStorage
          // This flag is the most reliable indicator of a service booking
          const isServiceBooking = localStorage.getItem('isServiceBooking') === 'true';
          const storedPaymentList = localStorage.getItem('paymentList');
          
          // Determine paymentList based on order type and service booking flag
          // Priority: isServiceBooking flag > order type > stored paymentList
          if (isServiceBooking) {
            // If service booking flag is set, this is definitely a booking
            this.paymentList = 'booking';
            console.log('Payment list set to booking based on isServiceBooking flag');
          } else if (res.result && res.result.orderType === 'SERVICE') {
            // If order type is SERVICE, this is a booking
            this.paymentList = 'booking';
            console.log('Updated payment list type to booking based on order type');
          } else if (res.result && res.result.orderType === 'PRODUCT') {
            // If order type is PRODUCT, this is a product order
            this.paymentList = 'product';
            console.log('Updated payment list type to product based on order type');
          } else if (storedPaymentList) {
            // Fallback to stored value if order type is not available
            this.paymentList = storedPaymentList;
            console.log('Using stored payment list type:', storedPaymentList);
          }
          
          // Clear the localStorage items
          localStorage.removeItem('paymentMethod');
          localStorage.removeItem('orderTimestamp');
          localStorage.removeItem('lastOrderId');
          localStorage.removeItem('from_yoco_payment');
          localStorage.removeItem('isServiceBooking');
          
          // Store the payment list type to ensure correct redirection
          localStorage.setItem('paymentList', this.paymentList);
          
          // Show success message to user
          this.commonService.successToast('Payment successful! Your order has been placed.');

          // Clear the cart ONLY if ALL of these conditions are met:
          // 1. This is NOT a service booking (isServiceBooking flag is false)
          // 2. Order type is explicitly 'PRODUCT' (not SERVICE or undefined)
          // 3. paymentList is 'product'
          const isProductOrder = 
            !isServiceBooking && 
            res.result && 
            res.result.orderType === 'PRODUCT' &&
            this.paymentList === 'product';
          
          if (isProductOrder) {
            console.log('Clearing cart for product order - all conditions met');
          this.clearCart();
          } else {
            console.log('Skipping cart clear - conditions:', {
              isServiceBooking,
              orderType: res.result?.orderType,
              paymentList: this.paymentList,
              reason: isServiceBooking ? 'Service booking detected' : 
                      (res.result?.orderType !== 'PRODUCT') ? 'Order type is not PRODUCT' : 
                      'Payment list is not product'
            });
          }
          
          // No automatic redirect - user will use buttons on the success page
        } else {
          console.error('Failed to update order status:', res.responseMessage);
          this.commonService.errorToast('There was an issue updating your order. Please check your order history.');
        }
      },
      (err) => {
        this.commonService.hideSpinner();
        console.error('Error updating order status:', err);
        this.commonService.errorToast('Error updating order status. Please contact support.');
      }
    );
  }

  /**
   * Clear the cart by removing all items after successful payment
   */
  clearCart() {
    // Fetch the cart list
    this.commonService.getApi('user/cartList', 1).subscribe((res: any) => {
      if (res.responseCode === 200 && res.result && res.result.cartList && res.result.cartList.length > 0) {
        const cartItems = res.result.cartList;
        let removedCount = 0;
        if (cartItems.length === 0) {
          // Nothing to clear; silently return
          return;
        }
        cartItems.forEach((item, idx) => {
          this.commonService.deleteApi(`user/removeItemFromCart?_id=${item._id}`, 1).subscribe((delRes: any) => {
            removedCount++;
            // When all items are cleared we just finish silently
            if (removedCount === cartItems.length) {
              return;
            }
          }, (err) => {
            // Even if one fails, continue with others
            removedCount++;
            if (removedCount === cartItems.length) {
              return;
            }
          });
        });
      } else {
        // Cart already empty; no user toast needed
      }
    }, (err) => {
      this.commonService.errorToast('Could not fetch cart to clear after payment.');
    });
  }

  /**
   * Refresh wallet transactions to ensure the latest transaction appears in the list
   */
  refreshWalletTransactions() {
    console.log('Starting wallet refresh for paymentList:', this.paymentList);
    this.commonService.showSpinner();
    
    // Get payment details from localStorage
    const paymentMethod = localStorage.getItem('paymentMethod') || 'Unknown';
    const paymentAmount = localStorage.getItem('paymentAmount') || '0';
    
    console.log('Payment details:', { method: paymentMethod, amount: paymentAmount });
    
    this.commonService.getApi(`deliveryOrders/getWalletTransaction?isCommission=true&page=1&limit=10`, 1).subscribe(
      (res: any) => {
        this.commonService.hideSpinner();
        console.log('Wallet API response:', res);
        
        if (res['responseCode'] == 200) {
          console.log('Wallet transactions:', res.result.docs);
          console.log('Wallet balance:', res.result.wallet);
          
          // Show success message with payment details
          const formattedAmount = new Intl.NumberFormat('en-ZA', { 
            style: 'currency', 
            currency: 'ZAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2 
          }).format(Number(paymentAmount));
          
          this.commonService.successToast(`R ${paymentAmount} has been successfully added to your wallet via ${paymentMethod} payment.`);
          
          // Clear payment-related localStorage items
          localStorage.removeItem('paymentMethod');
          localStorage.removeItem('paymentAmount');
          
          // Set flag for wallet component to refresh data
          localStorage.setItem('returningFromPayment', 'true');
        } else {
          console.error('Failed to refresh wallet transactions:', res['responseMessage']);
          this.commonService.errorToast('Failed to refresh wallet transactions. Please check your wallet page.');
        }
      },
      (err) => {
        this.commonService.hideSpinner();
        console.error('Error refreshing wallet transactions:', err);
        this.commonService.errorToast('Error refreshing wallet data. Please check your wallet page.');
        
        // Still set the returning flag so the wallet page will try to refresh
        localStorage.setItem('returningFromPayment', 'true');
      }
    );
  }

  /**
   * Set flag to indicate returning from payment success page
   */
  setReturningFlag() {
    localStorage.setItem('returningFromPayment', 'true');
  }
}
